/**
 * page-types/types.js - Page Type Definitions
 * Location: /app/frontend/src/admin/page_builder/page-types/
 * 
 * Defines the PageType enum and PageTypeConfig interface.
 * Page Builder is the canonical authority for all page layouts.
 */

/**
 * Page Type enum - All supported page types in Trap Comix
 */
export const PageType = {
  // Discovery & Navigation
  HOMEPAGE: 'homepage',
  DISCOVERY: 'discovery',
  
  // Creator Pages
  CREATOR_PROFILE: 'creator_profile',
  
  // Content Pages
  COMIC_SERIES: 'comic_series',
  NOVEL_SERIES: 'novel_series', // Aligned with comic_series naming
  GALLERY: 'gallery',
  
  // Reader Presentations (locked single-section)
  READER_VERTICAL: 'reader_vertical',
  READER_HORIZONTAL: 'reader_horizontal',
  READER_PAGE_BY_PAGE: 'reader_page_by_page',
  
  // Rankings & Community
  LEADERBOARD: 'leaderboard',
  COLLABORATION: 'collaboration',
  
  // Utility Pages
  STATIC: 'static', // About, FAQ, Policies, Guidelines
};

/**
 * Human-readable labels for page types
 */
export const PageTypeLabel = {
  [PageType.HOMEPAGE]: 'Homepage',
  [PageType.DISCOVERY]: 'Discovery / Browse',
  [PageType.CREATOR_PROFILE]: 'Creator Profile',
  [PageType.COMIC_SERIES]: 'Comic Series',
  [PageType.NOVEL_SERIES]: 'Novel Series',
  [PageType.GALLERY]: 'Art Gallery',
  [PageType.READER_VERTICAL]: 'Vertical Reader',
  [PageType.READER_HORIZONTAL]: 'Horizontal Reader',
  [PageType.READER_PAGE_BY_PAGE]: 'Page-by-Page Reader',
  [PageType.LEADERBOARD]: 'Leaderboard',
  [PageType.COLLABORATION]: 'Collaboration / Group',
  [PageType.STATIC]: 'Static Page',
};

/**
 * Page type descriptions for admin UI
 */
export const PageTypeDescription = {
  [PageType.HOMEPAGE]: 'Main landing page and discovery hub',
  [PageType.DISCOVERY]: 'Content exploration and filtering',
  [PageType.CREATOR_PROFILE]: 'Showcase creator and their work',
  [PageType.COMIC_SERIES]: 'Comic series hub with episode list',
  [PageType.NOVEL_SERIES]: 'Novel series with chapter navigation',
  [PageType.GALLERY]: 'Art portfolio and artwork showcase',
  [PageType.READER_VERTICAL]: 'Webtoon-style vertical scroll reading',
  [PageType.READER_HORIZONTAL]: 'Landscape/side-scroll panel reading',
  [PageType.READER_PAGE_BY_PAGE]: 'Traditional page-by-page navigation',
  [PageType.LEADERBOARD]: 'Rankings and achievement displays',
  [PageType.COLLABORATION]: 'Team projects and community spaces',
  [PageType.STATIC]: 'Static content pages (About, FAQ, Policies)',
};

/**
 * Icon mapping for page types (Lucide icon names)
 */
export const PageTypeIcon = {
  [PageType.HOMEPAGE]: 'home',
  [PageType.DISCOVERY]: 'compass',
  [PageType.CREATOR_PROFILE]: 'user',
  [PageType.COMIC_SERIES]: 'book-open',
  [PageType.NOVEL_SERIES]: 'file-text',
  [PageType.GALLERY]: 'image',
  [PageType.READER_VERTICAL]: 'arrow-down',
  [PageType.READER_HORIZONTAL]: 'arrow-right',
  [PageType.READER_PAGE_BY_PAGE]: 'book',
  [PageType.LEADERBOARD]: 'trophy',
  [PageType.COLLABORATION]: 'users',
  [PageType.STATIC]: 'file',
};

/**
 * Template categories for allowedSections filtering
 */
export const TemplateCategory = {
  HERO: 'hero',
  CONTENT: 'content',
  READER: 'reader',
  CREATOR: 'creator',
  CTA: 'cta',
  GALLERY: 'gallery', // New category for gallery-specific templates
};

/**
 * @typedef {Object} PageTypeConfig
 * @property {string} id - PageType enum value
 * @property {string} name - Display name
 * @property {string} description - Admin-facing description
 * @property {string} icon - Lucide icon name
 * @property {string[]} defaultSections - Template IDs spawned on create
 * @property {string[]} allowedCategories - Template categories that can be added
 * @property {string[]} allowedSections - Specific template IDs that can be added (computed from categories)
 * @property {number} minSections - Minimum sections (default: 1)
 * @property {number} maxSections - Maximum sections (default: 20)
 * @property {boolean} allowReorder - Can sections be reordered?
 * @property {boolean} allowAddRemove - Can sections be added/removed?
 * @property {boolean} isReader - Is this a locked single-section reader?
 */

/**
 * Check if a page type is a reader (locked single-section)
 * @param {string} pageType - PageType value
 * @returns {boolean}
 */
export function isReaderPageType(pageType) {
  return [
    PageType.READER_VERTICAL,
    PageType.READER_HORIZONTAL,
    PageType.READER_PAGE_BY_PAGE,
  ].includes(pageType);
}

/**
 * Get all page types as array for UI selectors
 * @returns {Array<{value: string, label: string, description: string, icon: string}>}
 */
export function getPageTypeOptions() {
  return Object.values(PageType).map(type => ({
    value: type,
    label: PageTypeLabel[type],
    description: PageTypeDescription[type],
    icon: PageTypeIcon[type],
    isReader: isReaderPageType(type),
  }));
}

/**
 * Get non-reader page types (for "New Page" selector)
 * Readers are typically created from content pages, not standalone
 */
export function getCreatablePageTypes() {
  return getPageTypeOptions().filter(opt => !opt.isReader);
}

export default {
  PageType,
  PageTypeLabel,
  PageTypeDescription,
  PageTypeIcon,
  TemplateCategory,
  isReaderPageType,
  getPageTypeOptions,
  getCreatablePageTypes,
};
